import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Auth slice state + actions (task 7.5, public-website spec).
 *
 * Per design §3.1 (Slices layer) the auth slice holds the
 * SPA's authentication lifecycle for the public Cognito user
 * pool's PKCE flow:
 *
 *  - `'unauthenticated'` (initial) — no token in `sessionStorage`,
 *    no Cognito session.
 *  - `'signing-in'` — user clicked "Continue with Amazon" /
 *    "Continue with Google" on `<SignInPage>` (task 7.6); the SPA
 *    has redirected to the Hosted UI and is awaiting the OAuth
 *    callback.
 *  - `'authenticated'` — the OAuth callback exchange succeeded
 *    (or `authSessionRestored` rehydrated a still-valid token from
 *    `sessionStorage` on app boot per design §2.3), and
 *    `idToken`, `accessToken`, `cognitoSub` are populated.
 *  - `'failed'` — the callback exchange returned an error or the
 *    Hosted UI redirected back with `error=...`; `error` carries
 *    the message.
 *
 * Tokens live in `sessionStorage` (one tab + one session) per
 * design §2.3, NEVER `localStorage`. The slice mirrors the
 * `sessionStorage` state in memory so React components can
 * subscribe via `useAppSelector` without re-reading storage on
 * every render.
 *
 * `cognitoSub` is the Cognito `sub` claim — the stable identifier
 * the Profile API keys against per Requirement 6 / design §2.3.
 *
 * Reducers stay pure — Redux Toolkit's `createSlice` wraps every
 * mutation in Immer so the assignments below are safe inside the
 * `produce` proxy and produce a new immutable state object.
 *
 * Function-length budget: every reducer below stays under 20
 * lines per Requirement 3.7.
 */

/** Authentication lifecycle discriminator per design §3.1. */
export type AuthStatus =
  | 'unauthenticated'
  | 'signing-in'
  | 'authenticated'
  | 'failed';

/** Auth slice state shape per design §3.1. */
export interface AuthState {
  /** Current lifecycle phase. */
  readonly status: AuthStatus;
  /** Cognito ID token (JWT) — bearer token for `ProfileApi.*V1`. */
  readonly idToken?: string;
  /** Cognito access token — used for Hosted UI sign-out. */
  readonly accessToken?: string;
  /** Cognito `sub` claim — stable identifier for the Profile API. */
  readonly cognitoSub?: string;
  /** Last sign-in error message (only set when `status === 'failed'`). */
  readonly error?: string;
}

/**
 * Stable payload shape for `authSessionRestored` and `authSignedIn`.
 * Both actions transition to the `'authenticated'` state and
 * populate the same three fields.
 */
export interface AuthSignedInPayload {
  readonly idToken: string;
  readonly accessToken: string;
  readonly cognitoSub: string;
}

const initialState: AuthState = {
  status: 'unauthenticated',
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Rehydrate a still-valid Cognito session from
     * `sessionStorage` on app boot per design §2.3. Sets
     * `status='authenticated'` and stores the three token fields
     * verbatim. Any prior `error` is cleared since rehydration
     * supersedes the previous lifecycle.
     */
    authSessionRestored(state, action: PayloadAction<AuthSignedInPayload>) {
      state.status = 'authenticated';
      state.idToken = action.payload.idToken;
      state.accessToken = action.payload.accessToken;
      state.cognitoSub = action.payload.cognitoSub;
      state.error = undefined;
    },
    /**
     * User clicked "Continue with Amazon" / "Continue with
     * Google" on `<SignInPage>` (task 7.6). Sets
     * `status='signing-in'` to drive any in-flight UI; the SPA
     * is about to redirect to the Hosted UI. Clears any prior
     * `error` so the next callback's outcome can land cleanly.
     */
    authSignInRequested(state) {
      state.status = 'signing-in';
      state.error = undefined;
    },
    /**
     * The OAuth callback exchange succeeded. Sets
     * `status='authenticated'`, stores the three token fields,
     * and clears any prior `error` per the task brief.
     */
    authSignedIn(state, action: PayloadAction<AuthSignedInPayload>) {
      state.status = 'authenticated';
      state.idToken = action.payload.idToken;
      state.accessToken = action.payload.accessToken;
      state.cognitoSub = action.payload.cognitoSub;
      state.error = undefined;
    },
    /**
     * User signed out via the AppShell sign-out flow per
     * Requirement 5.8. Clears every field and returns the slice
     * to its initial `'unauthenticated'` state. The
     * `sessionStorage` purge is the caller's responsibility per
     * design §2.3.
     */
    authSignedOut(state) {
      state.status = 'unauthenticated';
      state.idToken = undefined;
      state.accessToken = undefined;
      state.cognitoSub = undefined;
      state.error = undefined;
    },
    /**
     * The OAuth callback exchange returned an error or the
     * Hosted UI redirected back with `error=...`. Sets
     * `status='failed'` and stores the message.
     */
    authSignInFailed(state, action: PayloadAction<{ error: string }>) {
      state.status = 'failed';
      state.error = action.payload.error;
    },
  },
});

export const {
  authSessionRestored,
  authSignInRequested,
  authSignedIn,
  authSignedOut,
  authSignInFailed,
} = authSlice.actions;

export default authSlice.reducer;
